/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-use-before-define */
import { type ReactNode } from 'react';
import * as React from 'react';
import { BLOCKS } from '@contentful/rich-text-types';
import { motion } from 'framer-motion';
import { graphql } from 'gatsby';
import { rgba } from 'polished';
import styled, { css, useTheme } from 'styled-components';

import { RichText } from '@/components/global/RichText';
import { SEO } from '@/components/global/SEO';
import Page from '@/components/v2/Page';
import { V1GridLines, V1PageWrapper } from '@/components/v2/Page/V1PageWrapper';
import useMediaQuery from '@/utils/useMediaQuery';

import {
  Banner,
  Button,
  Heading,
  RunningText
} from '../components/design-system';
import { StickyCTA } from '../components/global/StickyCTA';
import { ReactComponent as ArrowRight } from '../images/icons/icon-arrow-right.svg';
import { ReactComponent as QuoteIcon } from '../images/icons/icon-quote.svg';
import { ANIMATIONS, INTERNAL_LINKS, STICKY } from '../utils';

const Wrapper = styled(motion.div)`
  padding: 0 40px;
  margin: 0 auto;
  margin-bottom: 200px;
  max-width: ${({ theme }) => theme.maxWidth.PAGE};
`;

const Inner = styled.div`
  width: 100%;
  margin: 0 auto;
`;

const Top = styled.div`
  display: inline-flex;
  align-items: flex-start;
  flex-direction: column;
`;

const BannerText = styled(Banner)`
  margin-bottom: -10px;
  margin-left: 11px;
  padding: 0 3px;
  background: ${({ theme }) => theme.colors.BLACK};
  z-index: 1;

  svg {
    transform: rotate(180deg);
    margin-right: 6px;

    path {
      fill: ${({ theme }) => theme.highlightColor};
    }
  }
`;

const Category = styled.div`
  border: 1px solid ${({ theme }) => theme.borderColor.DEFAULT};
  font-size: 24px;
  font-weight: 600;
  padding: 14px 12px;
  margin-bottom: 26px;
`;

const ContentWrapper = styled.div`
  display: flex;
`;

const Aside = styled.aside`
  postion: relative;
  margin: 0 0 30px 60px;
`;

const Content = styled.div`
  padding-left: 50px;
  position: relative;

  > svg {
    position: absolute;
    top: 7px;
    left: 6px;
  }

  &::before {
    content: '';
    position: absolute;
    top: 32px;
    left: 14px;
    bottom: -10px;
    flex: 1 1 auto;
    display: flex;
    width: 1px;
    background: linear-gradient(
      0deg,
      transparent 0%,
      ${({ theme }) => rgba(theme.highlightColor, 0.5)} 95%
    );
  }
`;

const StoryHeading = styled(Heading).attrs({ renderAs: 'h1' })`
  margin-bottom: 30px;
`;

const Quote = styled.div`
  font-size: 22px;
  line-height: 32px;
  margin-bottom: 25px;
`;

const Divider = styled.hr`
  height: 1px;
  background-color: ${({ theme }) => rgba(theme.highlightColor, 0.15)};
  margin: 0;
`;

const TextWrapper = styled(RunningText)`
  max-width: none;
  padding-top: 30px;

  p {
    color: ${({ theme }) => theme.fontColor.DEFAULT};
  }
`;

const Author = styled.div`
  padding-top: 30px;

  p:first-child {
    color: ${({ theme }) => theme.colors.WHITE};
    font-size: ${({ theme }) => theme.fontSize.MEDIUM};
    font-weight: 600;
    margin-bottom: 5px;
  }

  p:last-child {
    color: ${({ theme }) => theme.fontColor.MUTED};
    line-height: 18px;
  }
`;

interface StoryData {
  author: string;
  content: {
    raw: string;
  };
  customer: {
    logo: {
      url: string;
    };
    name: string;
    slug: string;
    website: string;
  };
  description: {
    description: string;
  };
  quote: {
    quote: string;
  };
  socialImage?: {
    url: string;
  };
}

interface CustomerStoryTemplateProps {
  data: {
    contentfulCustomerStory: StoryData;
  };
}

const ContentHeading = ({ children }: { children: ReactNode }) => (
  <Heading
    css={`
      padding-top: 10px;
      margin-bottom: 20px;
    `}
    renderAs="h3">
    {children}
  </Heading>
);

const options = {
  renderNode: {
    [BLOCKS.HEADING_1]: (node: any, children: ReactNode) => (
      <ContentHeading>{children}</ContentHeading>
    ),
    [BLOCKS.HEADING_2]: (node: any, children: ReactNode) => (
      <ContentHeading>{children}</ContentHeading>
    ),
    [BLOCKS.HEADING_3]: (node: any, children: ReactNode) => (
      <ContentHeading>{children}</ContentHeading>
    )
  }
};

const CustomerStoryTemplate: React.ComponentType<CustomerStoryTemplateProps> =
  React.memo(({ data }) => {
    const theme = useTheme();
    const shouldCollapse = useMediaQuery(theme.mediaQueryString.MEDIUM);
    const storyData = data.contentfulCustomerStory;

    return (
      <Page
        seo={
          <SEO
            description={storyData.description.description}
            image={storyData.socialImage?.url}
            title={`${storyData.customer.name} - Customer spotlight`}
            url={`${INTERNAL_LINKS.Customers}${storyData.customer.slug}/`}
          />
        }>
        <V1GridLines>
          <V1PageWrapper>
            <Wrapper {...ANIMATIONS.SimpleFadeIn}>
              <Inner>
                <Top>
                  <BannerText to="/customers/">
                    <ArrowRight />
                    <span>Customers</span>
                  </BannerText>
                  <Category>Customer spotlight</Category>
                </Top>
                {/* @ts-expect-error todo */}
                <StoryHeading>{storyData.customer.name}</StoryHeading>
                <ContentWrapper className={STICKY.Boundary}>
                  <Content>
                    <QuoteIcon />
                    <Quote>{storyData.quote.quote}</Quote>
                    <Divider />
                    <TextWrapper>
                      <RichText
                        body={storyData.content}
                        renderNodeOverrides={options.renderNode}
                      />
                    </TextWrapper>
                    <Author>
                      <p>{storyData.author}</p>
                      <p>{storyData.customer.name}</p>
                    </Author>
                  </Content>
                  {!shouldCollapse && (
                    <Aside>
                      <DetailsTout pos="top" storyData={storyData} />
                      <StickyCTA
                        heading="Want to learn how to accelerate time to insights?"
                        label="Request a demo"
                      />
                    </Aside>
                  )}
                </ContentWrapper>
                {shouldCollapse && (
                  <>
                    <DetailsTout pos="bottom" storyData={storyData} />
                    <StickyCTA
                      disabled={true}
                      heading="Want to learn how to accelerate time to insights?"
                      label="Request a demo"
                    />
                  </>
                )}
              </Inner>
            </Wrapper>
          </V1PageWrapper>
        </V1GridLines>
      </Page>
    );
  });

const DetailsWrapper = styled.div<{ $pos: string }>`
  position: relative;
  z-index: 1;
  max-width: none;
  margin: 0 0 24px 0;
  width: 280px;
  border: 1px solid ${({ theme }) => theme.borderColor.DEFAULT};
  padding: 20px 25px 30px 35px;

  p {
    margin-bottom: 20px;
  }

  ${({ $pos }) =>
    $pos === 'bottom' &&
    css`
      display: block;
      width: 100%;
      margin: 40px 0 24px 0;
    `}
`;

const CustomerLogo = styled.div`
  display: flex;
  margin-bottom: 10px;
`;

interface DetailsToutProps {
  pos: 'top' | 'bottom';
  storyData: StoryData;
}

const DetailsTout: React.ComponentType<DetailsToutProps> = React.memo(
  ({ storyData, pos = 'top' }) => {
    return (
      <DetailsWrapper $pos={pos}>
        <CustomerLogo>
          <img
            alt={storyData.customer.name}
            src={storyData.customer.logo.url}
          />
        </CustomerLogo>
        <p>{storyData.description.description}</p>
        <Button
          link={storyData.customer.website}
          size="small"
          text="Visit website"
        />
      </DetailsWrapper>
    );
  }
);

export const pageQuery = graphql`
  query ($slug: String!) {
    contentfulCustomerStory(customer: { slug: { eq: $slug } }) {
      author
      content {
        raw
        references {
          __typename
          ... on ContentfulBlogCallout {
            contentful_id
            body {
              raw
            }
          }

          ... on ContentfulBlogHint {
            contentful_id
            body {
              raw
            }
          }

          ... on ContentfulBlogQuote {
            contentful_id
            body {
              body
            }
          }

          ... on ContentfulBlogHexEmbed {
            contentful_id
            source
            height
          }

          ... on ContentfulBlogFloatImage {
            contentful_id
            image {
              url
              title
            }
            offset
            position
          }

          ... on ContentfulAsset {
            contentful_id
            url
            title
            mimeType
            description
            gatsbyImageData(placeholder: BLURRED, width: 740)
          }

          ... on ContentfulBlogLoomVideo {
            contentful_id
            url
          }
          ... on ContentfulBlogHtml {
            contentful_id
            markup {
              markup
            }
          }

          ... on ContentfulAudioSnippet {
            contentful_id
            audioFile {
              url
            }
            startTimeStamp
            endTimeStamp
          }
        }
      }
      quote {
        quote
      }
      description {
        description
      }
      socialImage {
        url
      }
      customer {
        slug
        website
        name
        logo {
          url
        }
      }
    }
  }
`;

export default CustomerStoryTemplate;
